import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { FormField } from '../features/surveys/types';
import { OptionsEditor } from './OptionsEditor';
import { RootState } from '../store/store';
import { useAppDispatch } from '../store/hooks';
import { loadFieldGroups } from '../features/surveys/surveyFieldGroupsSlice';

interface Props {
  field: FormField;
  onChange: (field: FormField) => void;
  onDelete: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  showGroups?: boolean;
}

export function SurveyFieldEditor({
  field,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  showGroups = true,
}: Props) {
  const [localField, setLocalField] = useState<FormField>(field);
  const { items: groups } = useSelector((state: RootState) => state.surveyFieldGroups);
  const { id: surveyId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (showGroups && surveyId) {
      dispatch(loadFieldGroups({ surveyId }));
    }
  }, [dispatch, showGroups, surveyId]);

  useEffect(() => {
    setLocalField(field);
  }, [field]);

  const handleChange = (updates: Partial<FormField>) => {
    const updated = { ...localField, ...updates };
    setLocalField(updated);
    onChange(updated);
  };

  return (
    <div className="border border-border rounded-lg p-4 space-y-4 bg-card">
      <div className="flex justify-between items-start">
        <div className="flex-1 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Tipo de Campo *
              </label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground"
                value={localField.type}
                onChange={(e) => {
                  const newType = e.target.value as FormField['type'];
                  const typesWithOptions = ['select', 'radio', 'checkbox-multiple'];
                  const updated: FormField = {
                    ...localField,
                    type: newType,
                    // Mantener opciones solo si el nuevo tipo también las requiere
                    options: typesWithOptions.includes(newType) ? localField.options : undefined,
                  };
                  handleChange(updated);
                }}
              >
                <option value="text">Texto</option>
                <option value="textarea">Área de Texto</option>
                <option value="number">Número</option>
                <option value="date">Fecha</option>
                <option value="select">Selección (Select)</option>
                <option value="radio">Selección Única (Radio)</option>
                <option value="checkbox-multiple">Selección Múltiple (Checkboxes)</option>
                <option value="checkbox">Casilla de Verificación</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Etiqueta *
              </label>
              <input
                type="text"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground"
                value={localField.label}
                onChange={(e) => handleChange({ label: e.target.value })}
                placeholder="Ej: Nombre completo"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {showGroups && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Agrupación (Área)
                </label>
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground"
                  value={localField.groupId || ''}
                  onChange={(e) => handleChange({ groupId: e.target.value || undefined })}
                >
                  <option value="">Sin agrupación (Estadístico)</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name} ({group.weight}%)
                    </option>
                  ))}
                </select>
              </div>
            )}

            {localField.type !== 'checkbox' && localField.type !== 'radio' && localField.type !== 'checkbox-multiple' && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Placeholder
                </label>
                <input
                  type="text"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground"
                  value={localField.placeholder || ''}
                  onChange={(e) => handleChange({ placeholder: e.target.value || undefined })}
                  placeholder="Texto de ayuda..."
                />
              </div>
            )}
          </div>

          {(localField.type === 'select' || localField.type === 'radio' || localField.type === 'checkbox-multiple') && (
            <OptionsEditor
              options={localField.options || []}
              onChange={(options) => handleChange({ options: options.length > 0 ? options : undefined })}
            />
          )}

          {(localField.type === 'number' || localField.type === 'text') && (
            <div className="grid grid-cols-2 gap-4">
              {localField.type === 'number' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Valor Mínimo
                    </label>
                    <input
                      type="number"
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground"
                      value={localField.validation?.min ?? ''}
                      onChange={(e) => {
                        const min = e.target.value ? Number(e.target.value) : undefined;
                        handleChange({
                          validation: { ...localField.validation, min },
                        });
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Valor Máximo
                    </label>
                    <input
                      type="number"
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground"
                      value={localField.validation?.max ?? ''}
                      onChange={(e) => {
                        const max = e.target.value ? Number(e.target.value) : undefined;
                        handleChange({
                          validation: { ...localField.validation, max },
                        });
                      }}
                    />
                  </div>
                </>
              )}
              {localField.type === 'text' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Patrón (Regex)
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground"
                    value={localField.validation?.pattern || ''}
                    onChange={(e) => {
                      const pattern = e.target.value || undefined;
                      handleChange({
                        validation: { ...localField.validation, pattern },
                      });
                    }}
                    placeholder="Ej: ^[A-Za-z]+$"
                  />
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={localField.required}
                onChange={(e) => handleChange({ required: e.target.checked })}
                className="rounded border-input"
              />
              <span className="text-sm text-foreground">Campo requerido</span>
            </label>
          </div>
        </div>

        <div className="flex flex-col gap-2 ml-4">
          {canMoveUp && (
            <button
              type="button"
              onClick={onMoveUp}
              className="p-2 rounded-md border border-input bg-background hover:bg-muted transition-colors"
              title="Mover arriba"
            >
              ↑
            </button>
          )}
          {canMoveDown && (
            <button
              type="button"
              onClick={onMoveDown}
              className="p-2 rounded-md border border-input bg-background hover:bg-muted transition-colors"
              title="Mover abajo"
            >
              ↓
            </button>
          )}
          <button
            type="button"
            onClick={onDelete}
            className="p-2 rounded-md border border-destructive bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
            title="Eliminar campo"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}



